import {
  HttpClient,
  HttpErrorResponse,
  HttpEventType,
  HttpProgressEvent,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  catchError,
  filter,
  from,
  map,
  Observable,
  switchMap,
  tap,
} from 'rxjs';
import { S3BucketName } from 'src/app/shared/models/constants/s3-bucket-name';
import { S3UploadURLResponseDTO } from 'src/app/shared/models/dtos/responses/s3-url-response-dto/s3-upload-url-response-dto';
import {
  MigrationUploadFile,
  MigrationUploadFileStatus,
} from 'src/app/shared/models/migration-upload-file/migration-upload-file';
import { downloadFromBlob, renameFile } from 'src/app/utils/file';
import { oneSecond } from 'src/app/utils/time';
import { DataService } from '../../api/data/data.service';
import { SnackBarService } from '../snackbar/snackbar.service';
import { TranslationService } from '../translation/translation.service';
import { DuplicateFileNameException } from 'src/app/shared/errors/product-service/product-service-errors';
import { UserService } from '../../api/user/user.service';

@Injectable({
  providedIn: 'root',
})
/**
 * S3UploadService manages uploading files to some URL that is fetched from
 * backend service
 */
export class S3UploadService {
  currentlyUploading: MigrationUploadFile[] = [];
  feedbackMessages = {
    fileUploadSuccess: 'file-upload-success',
    fileUploadError: 'file-upload-error',
    fileUploadDuplicate: 'file-upload-duplicate',
    startingUpload: 'starting-upload',
  };

  /**
   * @constructor
   */
  constructor(
    private readonly httpClient: HttpClient,
    private readonly dataService: DataService,
    private readonly userService: UserService,
    private readonly snackBarService: SnackBarService,
    private readonly translationService: TranslationService
  ) {
    translationService.translateObject(this.feedbackMessages);
  }

  /**
   * Gets a S3 presigned URL to perform upload
   * @param {MigrationUploadFile} migrationUploadFile
   * @return {Observable<Object>} the presigned url
   */
  private getPresignedUrl(
    migrationUploadFile: MigrationUploadFile
  ): Observable<S3UploadURLResponseDTO> {
    return this.fetchURL(migrationUploadFile).pipe(
      map((s3URLResponse) => {
        return new S3UploadURLResponseDTO(
          s3URLResponse.uploadUrl,
          s3URLResponse.newFileName,
          s3URLResponse.requestId
        );
      })
    );
  }

  /**
   * fetches s3 upload url from server
   * @param {MigrationUploadFile} migrationUploadFile
   * @return {Observable<S3UploadURLResponseDTO>}
   */
  private fetchURL(
    migrationUploadFile: MigrationUploadFile
  ): Observable<S3UploadURLResponseDTO> {
    return this.dataService.getS3UploadURL(
      migrationUploadFile.dataType,
      migrationUploadFile.file!.name,
      migrationUploadFile.dataType,
      migrationUploadFile.supplyType,
      migrationUploadFile.regionId
    );
  }

  /**
   * Uploads a file
   * @param {MigrationUploadFile} migrationUploadFile
   * @return {Observable<number | undefined>}
   */
  private _upload(
    migrationUploadFile: MigrationUploadFile
  ): Observable<number | undefined> {
    return this.getPresignedUrl(migrationUploadFile).pipe(
      catchError((err) => {
        if (err instanceof Error && err.message === 'already expired') {
          return from(this.getPresignedUrl(migrationUploadFile));
        }
        throw err;
      }),
      tap((s3URLResponse) => {
        migrationUploadFile.requestId = s3URLResponse.requestId;
      }),
      switchMap((s3URLResponse) => {
        const newFile = renameFile(
          migrationUploadFile.file!,
          s3URLResponse.newFileName
        );
        return this.httpClient
          .put(s3URLResponse.uploadUrl, newFile, {
            reportProgress: true,
            observe: 'events',
          })
          .pipe(
            catchError((err) => {
              if (err instanceof HttpErrorResponse && err.status === 403) {
                throw new Error('retry');
              } else {
                throw err;
              }
            }),
            filter(
              (event) =>
                event.type === HttpEventType.UploadProgress ||
                event.type === HttpEventType.Response
            ),
            map((event) => {
              if (event.type === HttpEventType.UploadProgress) {
                event = event as HttpProgressEvent;
                if (event.total) {
                  return (event.loaded / event.total) * 100;
                }
                return NaN;
              }
              return undefined;
            })
          );
      })
    );
  }

  /**
   * @param {number} tries
   * @param {MigrationUploadFile} migrationUploadFile
   */
  upload(tries: number, migrationUploadFile: MigrationUploadFile) {
    if (!migrationUploadFile.file) {
      throw new Error('MigrationUploadFile.file is required');
    }
    if (!this.currentlyUploading.includes(migrationUploadFile)) {
      this.currentlyUploading.push(migrationUploadFile);
    }
    let started = false;
    const upload$ = this._upload(migrationUploadFile).subscribe({
      next: (progress) => {
        if (!started) {
          this.snackBarService.info(this.feedbackMessages.startingUpload);
          started = true;
        }
        if (progress) {
          migrationUploadFile.status = MigrationUploadFileStatus.PROCESSING;
          migrationUploadFile.progress = progress;
        } else {
          migrationUploadFile.status = MigrationUploadFileStatus.UPLOADED;
        }
      },
      error: (err) => {
        if (err instanceof Error && err.message === 'retry' && tries < 3) {
          migrationUploadFile.status = MigrationUploadFileStatus.RETRYING;
          setTimeout(
            () => this.upload(tries + 1, migrationUploadFile),
            10 * oneSecond
          );
        }
        if (err instanceof DuplicateFileNameException) {
          this.snackBarService.error(this.feedbackMessages.fileUploadDuplicate);
          migrationUploadFile.status = MigrationUploadFileStatus.UPLOAD_ERROR;
        } else {
          this.snackBarService.error(this.feedbackMessages.fileUploadError);
          migrationUploadFile.status = MigrationUploadFileStatus.UPLOAD_ERROR;
          throw new Error(err);
        }
      },
      complete: () => {
        this.snackBarService.success(this.feedbackMessages.fileUploadSuccess);
      },
    });
    migrationUploadFile.upload$ = upload$;
    upload$.add(() => {
      migrationUploadFile.upload$ = undefined;
      migrationUploadFile.progress = undefined;
    });
  }

  /**
   * @param {number} page
   * @param {number} pageSize
   * @param {number[]} regionIds
   * @param {string} dataType
   * @return {Observable<MigrationUploadFile[]>}
   */
  getUploadQueue(
    page: number,
    pageSize: number,
    regionIds: number[],
    dataType: string
  ): Observable<MigrationUploadFile[]> {
    return this.dataService.getMigrationUploadQueue(
      page,
      pageSize,
      regionIds,
      dataType
    );
  }

  /**
   * @param {MigrationUploadFile} migrationFile migration file to get report for
   */
  download(migrationFile: MigrationUploadFile) {
    if (
      (!migrationFile.downloading &&
        migrationFile.status ===
          MigrationUploadFileStatus.PROCESSED_WITH_ERROR) ||
      migrationFile.dataType === S3BucketName.PORT_REPORT
    ) {
      migrationFile.downloading = true;
      this.dataService
        .getS3DownloadURL(migrationFile.requestId!)
        .pipe(
          switchMap((response: string) => {
            const presignedURL = new URL(response);

            return this.httpClient
              .get(response, {
                responseType: 'blob',
              })
              .pipe(
                map((data) => {
                  return {
                    data,
                    filename: presignedURL.pathname.slice(1),
                  };
                })
              );
          })
        )
        .subscribe(({ data, filename }) => {
          downloadFromBlob(filename, data);
        })
        .add(() => {
          migrationFile.downloading = false;
        });
    }
  }

  /**
   * Downloads migration template spreadsheet
   */
  downloadMigrationTemplate() {
    this.userService
      .downloadMigrationTemplate()
      .subscribe(({ data, filename }) => {
        downloadFromBlob(filename, data);
      });
  }
}
